/**
 * Save-file schema versioning + migration for the measurements JSON.
 *
 * The "Save" button downloads measurements as JSON. Every saved file carries a
 * `schemaVersion` so the format can evolve safely: on load we detect the file's
 * version and run it forward through the migration table to the current shape.
 * Files with no `schemaVersion` are legacy "version 0" and are upgraded on load.
 *
 * To evolve the format: bump `CURRENT_SCHEMA_VERSION`, add a `SaveDataVN` type,
 * point `SaveDataCurrent` at it, and register a `migrations[N-1]` step that turns
 * the previous shape into the new one.
 */

import type { TestResults } from '../types';

/** Bump this whenever the saved-file shape changes. */
export const CURRENT_SCHEMA_VERSION = 1;

/** UI-state fields persisted alongside measurements. */
export interface SaveUiState {
    noiseVolume?: number;
    contour?: string;
    sinePlaybackMode?: string;
    volumeDbStep?: string | number;
}

/** Config fields persisted alongside measurements (round-trip/diagnostic). */
export interface SaveAppConfig {
    resultUsageMinFrequency?: number;
    resultUsageMaxFrequency?: number;
}

/** Version 1 — the current canonical saved-file shape. */
export interface SaveDataV1 {
    schemaVersion: 1;
    /** ISO timestamp, diagnostic only. */
    savedAt?: string;
    testResults: TestResults;
    uiState: SaveUiState;
    appConfig?: SaveAppConfig;
}

/**
 * The current saved-file shape. Always points at the latest `SaveDataVN`, so the
 * rest of the app only ever imports this alias.
 */
export type SaveDataCurrent = SaveDataV1;

/**
 * Migration table keyed by *source* version: `migrations[n]` upgrades a
 * version-`n` object to version `n + 1`.
 */
const migrations: Record<number, (data: any) => any> = {
    0: migrateV0toV1,
};

/**
 * Upgrade a legacy (unversioned, flat) file to the canonical v1 shape.
 *
 * Legacy files wrote `noiseVolume`/`contour` and the freq range as top-level
 * keys; v1 nests them under `uiState`/`appConfig`. Any already-nested `uiState`
 * or `appConfig` on the input is preserved so partially-shaped files still
 * upgrade cleanly.
 */
function migrateV0toV1(data: any): SaveDataV1 {
    const src = (data ?? {}) as Record<string, unknown>;
    const legacyUiState = (src.uiState ?? {}) as SaveUiState;
    const legacyAppConfig = (src.appConfig ?? {}) as SaveAppConfig;

    const uiState: SaveUiState = {
        noiseVolume: legacyUiState.noiseVolume ?? (src.noiseVolume as number | undefined),
        contour: legacyUiState.contour ?? (src.contour as string | undefined),
        sinePlaybackMode: legacyUiState.sinePlaybackMode,
        volumeDbStep: legacyUiState.volumeDbStep,
    };

    const appConfig: SaveAppConfig = {
        resultUsageMinFrequency:
            legacyAppConfig.resultUsageMinFrequency ?? (src.resultUsageMinFrequency as number | undefined),
        resultUsageMaxFrequency:
            legacyAppConfig.resultUsageMaxFrequency ?? (src.resultUsageMaxFrequency as number | undefined),
    };

    return {
        schemaVersion: 1,
        savedAt: src.savedAt as string | undefined,
        testResults: src.testResults as TestResults,
        uiState,
        appConfig,
    };
}

/**
 * Outcome of a migration attempt. Exactly one of `data` / `error` is set.
 * Uses optional fields rather than a discriminated union because this project
 * builds with `strict: false` (no `strictNullChecks`), under which TypeScript
 * does not narrow a `{success}`-tagged union on its failure branch.
 */
export interface SaveMigrationResult {
    data?: SaveDataCurrent;
    error?: string;
}

/**
 * Migrate a parsed saved file up to the current schema version.
 *
 * Sets `error` (surfaced to the user via the load path's `onError`) when the
 * file was written by a newer app version, or when no migration path exists
 * from its version; otherwise sets `data` to the upgraded object.
 */
export function migrateSaveData(raw: unknown): SaveMigrationResult {
    const src = (raw ?? {}) as Record<string, unknown>;
    let version = typeof src.schemaVersion === 'number' ? src.schemaVersion : 0;

    if (version > CURRENT_SCHEMA_VERSION) {
        return {
            error: 'This file was saved by a newer version of Peace EQ. Please update the app to load it.',
        };
    }

    let data: any = raw;
    while (version < CURRENT_SCHEMA_VERSION) {
        const step = migrations[version];
        if (!step) {
            return { error: `No migration path from schema version ${version}.` };
        }
        data = step(data);
        version++;
    }

    return { data: data as SaveDataCurrent };
}

/**
 * Validate that an object matches the current canonical shape. Run *after*
 * `migrateSaveData`, so the migrated object is what gets checked. The essential
 * payload is `testResults` (three arrays); `uiState` must be an object but its
 * individual fields are optional.
 */
export function validateSaveData(data: unknown): data is SaveDataCurrent {
    if (!data || typeof data !== 'object') return false;
    const d = data as Record<string, unknown>;

    const tr = d.testResults;
    if (!tr || typeof tr !== 'object') return false;
    const trRec = tr as Record<string, unknown>;
    if (!Array.isArray(trRec.both) || !Array.isArray(trRec.left) || !Array.isArray(trRec.right)) {
        return false;
    }

    if (!d.uiState || typeof d.uiState !== 'object') return false;

    return true;
}
